import { CasinoGameList } from 'components/CasinoGameList/CasinoGameList';
import { Loader } from 'components/Loader/Loader';
import { filterGamesByProviders } from 'helpers/gameHelper';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLiveCasinoGamesPage } from 'services/api/gameApiService';
import { CasinoGame } from 'types/CasinoGame';
import useAsyncEffect from 'use-async-effect';
import styles from '../styles/pages/CasinoGames.module.css';
import { DeviceSpecificLayout } from 'components/Layout/DeviceSpecificLayout';
import { MobileLayoutProps } from 'components/mobile/MobileLayout/MobileLayout';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { CasinoTabs } from 'components/mobile/CasinoTabs/CasinoTabs';
import { usePlayerLayout } from 'lib/playerLayout';
import { CASINO_CATALOG_MIN_LOADER_MS } from 'config/constants';
import { waitMinimumElapsedSince } from 'utils/waitMinimumElapsedSince';
import { VipRoundButton } from 'components/mobile/VipChrome/VipChrome';
import { VipIcon } from 'components/mobile/VipChrome/VipIcons';
import { VipCasinoBand } from 'components/CasinoGameList/VipCasinoBand';

interface ProviderGames {
  provider: string;
  games: CasinoGame[];
}

export const LiveCasino = () => {
  const [allGames, setAllGames] = useState<ProviderGames[]>([]);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [currentSearch, setCurrentSearch] = useState('');
  const [searchResults, setSearchResults] = useState<ProviderGames[]>([]);
  const { isMobile } = useBreakpoint();

  useAsyncEffect(async (isMounted) => {
    const loadStartedAt = Date.now();
    try {
      const first = await getLiveCasinoGamesPage(1, true);
      await waitMinimumElapsedSince(
        loadStartedAt,
        CASINO_CATALOG_MIN_LOADER_MS
      );
      if (!isMounted()) {
        return;
      }
      setAllGames(filterGamesByProviders(first.items));
      setIsLoading(false);

      const pageCount = Math.max(1, Number(first.pageCount) || 1);
      if (pageCount <= 1) {
        return;
      }

      let merged = [...first.items];
      for (let p = 2; p <= pageCount; p++) {
        try {
          const next = await getLiveCasinoGamesPage(p);
          if (!isMounted()) {
            return;
          }
          merged = merged.concat(next.items);
          setAllGames(filterGamesByProviders(merged));
        } catch {
          // keep catalog from successful pages
        }
      }
    } catch {
      if (isMounted()) {
        setIsLoading(false);
      }
    }
  }, []);

  const handleSearch = (event: any) => {
    const value = event.target.value;
    setCurrentSearch(value);
    const results: ProviderGames[] = allGames.map((x) => ({
      provider: x.provider,
      games: x.games.filter((game) =>
        game.name.toLocaleLowerCase().includes(value.toLocaleLowerCase())
      ),
    }));
    setSearchResults(results);
  };

  const gameList = currentSearch ? searchResults : allGames;

  // The modern layout names the page in its header and tabs already.
  const layout = usePlayerLayout();
  const modern = layout === 'modern';
  const vip = layout === 'vip';
  // VIP: the search behind a button of the header, the name there too.
  const [vipSearch, setVipSearch] = useState(false);
  const mobileProps: MobileLayoutProps = {
    title: modern || vip ? undefined : t('live_casino_label'),
    headerTrailing: vip ? (
      <VipRoundButton
        label={t('search_label')}
        on={vipSearch}
        onClick={() => {
          setVipSearch(!vipSearch);
          setCurrentSearch('');
        }}
      >
        <VipIcon name="search" />
      </VipRoundButton>
    ) : undefined,
  };

  return (
    <DeviceSpecificLayout mobileLayoutProps={mobileProps}>
      <CasinoTabs />
      {vip && <VipCasinoBand live={true} providers={allGames} />}
      {(!vip || vipSearch) && (
        <div className={styles.searchWrapper}>
          <input
            type="text"
            className={styles.searchInput}
            onChange={handleSearch}
            value={currentSearch}
            placeholder={t('search_placeholder', 'Search games...')}
            autoFocus={vip}
          />
          {currentSearch && isMobile && (
            <div
              className={styles.cancelSearchIcon}
              onClick={() => setCurrentSearch('')}
            />
          )}
        </div>
      )}
      {isLoading ? (
        <Loader />
      ) : (
        gameList.length > 0 && (
          <CasinoGameList
            gameList={gameList}
            useLiveCasino
            searching={!!currentSearch}
          />
        )
      )}
    </DeviceSpecificLayout>
  );
};
